import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { i18n, useTranslation } from 'next-i18next';
import { NextSeo } from 'next-seo';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import Mobile from '@/components/Device/Mobile';
import Pc from '@/components/Device/PC';
import { SUBJECT_VALUE_MAP } from '@/components/Resources/constants';
import { ResourceProvider } from '@/components/Resources/context';
import ContentList from '@/components/Resources/Subject/ContentList';
import FilterConditionMobile from '@/components/Resources/Subject/FilterConditionMobile';
import FilterConditionPC from '@/components/Resources/Subject/FilterConditionPC';
import Menu from '@/components/Resources/Subject/Menu';
import Nav from '@/components/Resources/Subject/Nav';
import Title from '@/components/Resources/Subject/Title';
import { Resource, RootMenu } from '@/components/Resources/types';
import { getConvertMenuData } from '@/components/Resources/utils';
import useRemoveEn from '@/pages/useRemoveEn';
import {
  GetMenusRequest,
  GetMenusResponse,
  GetNavListResponse,
} from '@/request/interface/resources';
import { getMenus, getNavList } from '@/request/resources';
import useResourcesStore from '@/store/useResourcesStore';
import { capitalizeFirstLetter } from '@/utils/utils';

const LoginOrReserve = dynamic(
  () => import('@/components/Resources/Subject/LoginOrReserve'),
  { ssr: false },
);

const h1Label = {
  chinese: '国际中文资源',
  math: '国际数学资源',
  english: '英文读写资源',
};

const Home = () => {
  useRemoveEn();
  const state = useLocation();
  const { t } = useTranslation('resources');
  const router = useRouter();
  const { subject } = router.query;
  const subjectLabel =
    SUBJECT_VALUE_MAP[subject as 'english' | 'math' | 'chinese'];
  const pageTitle = t(`页面标题-分类页-${subjectLabel}`);
  const pageDescription = t(`页面描述-分类页-${subjectLabel}`);
  const h1Value = t(h1Label[subject as 'chinese' | 'math' | 'english']);
  // const showBanner = useShowBanner();
  const [convertMenus, setConvertMenus] = useState<RootMenu[]>();
  const resourceListRef = useRef<Resource[]>([]);
  const { setNavList, navList } = useResourcesStore(state => {
    return { setNavList: state.setNavList, navList: state.navList };
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    getNavList().then(list => {
      setNavList(list as GetNavListResponse['content']);
    });
  }, []);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    const menusParams = { subject: capitalizeFirstLetter(subject as string) };
    getMenus(menusParams as GetMenusRequest).then(response => {
      const menus = getConvertMenuData(response as GetMenusResponse['content']);
      setConvertMenus(menus);
    });
  }, [subject]);
  return (
    <>
      <NextSeo
        openGraph={{
          url: state?.href,
          title: pageTitle,
          description: pageDescription,
          images: [
            {
              url: 'https://cdnwukong.com/images/public/og/offical-site-1.png',
              width: 300,
              height: 300,
              alt: pageTitle,
            },
          ],
        }}
      />
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          data-n-head="ssr"
          data-hid="apple-mobile-web-app-title"
          name="apple-mobile-web-app-title"
          content={pageTitle}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: pageTitle, //取当前页title
              description: pageDescription, //取当前页description
              url: state?.href, //取当前页url

              mainEntity: {
                '@type': 'ItemList',
                itemListOrder: 'https://schema.org/Descending',
                name: h1Value, //取当前页H1
                numberOfItems: resourceListRef.current?.length || 0, //这里的具体数字按页面上实际展示的资源数量来，一页最多16个，最少1个；下方的展示完全，即这里是3个，下方对应到position 3.
                itemListElement: resourceListRef.current?.map(
                  (item, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                      '@type': 'LearningResource',
                      name: item.title,
                      url: `${state?.origin}${i18n?.language === 'en' ? '' : '/' + i18n?.language}/resources/${item.seoUrl}_${item.id}detail/`,
                      image: item.background,
                    },
                  }),
                ),
              },
            }),
          }}
        />
      </Head>
      <div className="tracking-[0.5px] mobile:mb-[80px] mobile:pt-[70px] pc:mb-[200px] pc:pt-[90px]">
        <div className="mx-auto my-0 lg:container  mobile:px-[20px]">
          {/* <Breadcrumbs /> */}
          <Title menuData={convertMenus} />
        </div>
        <LoginOrReserve />
        {convertMenus?.length ? (
          <ResourceProvider menus={convertMenus}>
            <>
              {!!navList && (
                <Nav data={navList as GetNavListResponse['content']} />
              )}
              <div className="mx-auto my-0 lg:container mobile:w-full  mobile:px-[20px]">
                <Pc>
                  <FilterConditionPC />
                </Pc>
                <Mobile>
                  <FilterConditionMobile data={convertMenus} />
                </Mobile>
                {/* {showBanner && (
                  <Mobile>
                    <div className="mb-[8px]">
                      <BannerMobile />
                    </div>
                  </Mobile>
                )} */}
                <div className="flex justify-between">
                  <Pc>
                    <Menu data={convertMenus} />
                  </Pc>
                  <ContentList
                    menuData={convertMenus}
                    getResourceList={data => (resourceListRef.current = data)}
                  />
                </div>
              </div>
            </>
          </ResourceProvider>
        ) : (
          <div className="flex h-[1215px] w-full items-center justify-center" />
        )}
      </div>
      {/* <Mobile>
          <ScrollToTop />
        </Mobile> */}
    </>
  );
};

export default Home;
